import { useChatStore } from '@/store/useChatStore';
// Dynamic color imports - commented out to always use Inner Mastery Guide Blue
// import { getCategoryPalette, normalizeCategory } from '@/utils/colors';
import { ChatMode } from '@/utils/constants';
import { Icon } from '@iconify/react';
import { Button, cn, Textarea, Tooltip } from '@nextui-org/react';
import type { CSSProperties } from 'react';
// import { useLocation } from 'react-router-dom';

const floatingTextStyle =
  'absolute bottom-[90px] left-10 right-10 rounded-lg shadow-lg transition-opacity duration-300 z-50';
const slideAnimation = 'transition-[width] duration-300 ease-in-out';
const fadeAnimation = 'transition-opacity duration-300';
const waveAnimationClass =
  'relative after:absolute after:inset-0 after:rounded-full after:animate-wave-ping after:bg-[color:var(--input-accent)] after:opacity-35 before:absolute before:inset-0 before:rounded-full before:animate-wave-ping-delayed before:bg-[color:var(--input-accent)] before:opacity-20';
const micAnimationClass = 'animate-pulse scale-110 transition-all duration-200';

interface AudioModeInputProps {
  isListening: boolean;
  isLoading: boolean;
  inputText: string;
  onToggleListening: () => void;
  onSendMessage: (text: string) => void;
  onClearInput: () => void;
  stopSpeaking: () => void;
}

export function AudioModeInput({
  isListening,
  isLoading,
  inputText,
  onToggleListening,
  onSendMessage,
  onClearInput,
  stopSpeaking,
}: AudioModeInputProps) {
  const { showSendButton, setChatMode, setEnableTTS } = useChatStore();
  // Dynamic color feature - commented out to always use Inner Mastery Guide Blue
  // const { selectedPrompt } = useChatStore();
  // const location = useLocation();

  // Dynamic color feature - commented out to always use Inner Mastery Guide Blue
  // const queryParams = new URLSearchParams(location.search);
  // const urlTag = queryParams.get('tag');
  // const categoryLabel =
  //   urlTag || selectedPrompt?.tag || selectedPrompt?.name || 'Action';
  // const palette = getCategoryPalette(normalizeCategory(categoryLabel));
  // const accentStyle = { '--input-accent': palette.signature } as CSSProperties;

  // Always use Inner Mastery Guide Blue
  const accentStyle = { '--input-accent': '#5B8FEA' } as CSSProperties;

  return (
    <>
      {isListening && inputText && showSendButton && (
        <div className={floatingTextStyle}>
          <Textarea
            value={isListening && inputText === '' ? 'Listening...' : inputText}
            readOnly
            classNames={{
              input:
                'text-sm text-default-700 dark:text-content-1 bordered break-words bg-white dark:bg-default-100',
              innerWrapper:
                'bg-white dark:bg-default-100 rounded-xl overflow-hidden',
              base: 'min-h-0 bg-white dark:bg-default-100 rounded-xl overflow-hidden',
            }}
            minRows={1}
            maxRows={10}
            radius="md"
            variant="bordered"
            style={{ outline: 'none', border: 'none' }}
            endContent={
              <Button
                isIconOnly
                size="md"
                variant="light"
                onPress={onClearInput}
              >
                <Icon
                  className="text-default-800"
                  icon="mdi:close"
                  width={20}
                />
              </Button>
            }
          />
        </div>
      )}

      <div
        className={`${slideAnimation} w-[280px] mx-auto ${fadeAnimation} opacity-100`}
      >
        <div
          className="flex pb-4 gap-4 justify-center w-[100%] items-center"
          style={accentStyle}
        >
          <div className="flex gap-4 bg-[white] border-2 dark:bg-default-200 p-4 rounded-full shadow-lg">
            <Tooltip
              content={isListening ? 'Stop recording' : 'Start recording'}
            >
              <Button
                isIconOnly
                size="sm"
                radius="lg"
                variant="solid"
                onPress={() => {
                  stopSpeaking();
                  onToggleListening();
                }}
                className={cn(
                  'bg-white border border-default-200 shadow-sm',
                  isListening && waveAnimationClass
                )}
                isLoading={isLoading}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      'relative z-10 text-[color:var(--input-accent)]',
                      isListening && micAnimationClass
                    )}
                    icon={isListening ? 'mdi:microphone-off' : 'mdi:microphone'}
                    width={18}
                  />
                </div>
              </Button>
            </Tooltip>
            {showSendButton && (
              <Tooltip content="Send message">
                <Button
                  isIconOnly
                  size="sm"
                  radius="lg"
                  variant="solid"
                  isDisabled={!inputText}
                  className="bg-[color:var(--input-accent)]"
                  onPress={() =>
                    inputText.trim() && onSendMessage(inputText.trim())
                  }
                >
                  <Icon
                    className={cn(
                      '[&>path]:stroke-[2px]',
                      !inputText ? 'text-white/60' : 'text-white'
                    )}
                    icon="solar:arrow-up-linear"
                    width={20}
                  />
                </Button>
              </Tooltip>
            )}
          </div>
          <Tooltip content="Exit full audio mode">
            <Button
              isIconOnly
              size="sm"
              radius="full"
              variant="light"
              onPress={() => {
                setChatMode(ChatMode.TEXT);
                stopSpeaking();
                setEnableTTS(false);
              }}
            >
              <Icon className="text-default-500" icon="mdi:close" width={24} />
            </Button>
          </Tooltip>
        </div>
      </div>
    </>
  );
}
